<template>
  <section class="flex flex-col gap-4 rounded-lg border p-4">
    <header class="flex items-center justify-between gap-3">
      <div class="flex items-center gap-2">
        <h3 class="text-base font-semibold text-foreground">
          <slot name="title"> Uploads </slot>
        </h3>
        <span class="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
          {{ uploadsCountLabel }}
        </span>
      </div>
      <UploadButton
        v-if="!readonly"
        :uploadable-id="props.uploadableId"
        :uploadable-type="props.uploadableType"
        :multiple="props.multiple"
        :images-only="props.imagesOnly"
        :file-types="props.fileTypes"
        :disabled="uploadableStore.isLoading"
        :label="props.uploadLabel"
        :uploading-label="props.uploadingLabel"
        @upload-start="handleUploadStart"
        @uploaded="handleUploaded"
        @error="handleUploadError"
      />
    </header>

    <p v-if="statusMessage" class="text-sm text-muted-foreground">
      {{ statusMessage }}
    </p>

    <p v-if="loadErrorMessage" class="text-sm text-destructive">
      {{ loadErrorMessage }}
    </p>

    <p v-if="uploadableStore.isLoading && uploadableStore.items.length === 0" class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">Loading uploads...</p>

    <p v-else-if="!uploadableStore.isLoading && uploadableStore.items.length === 0" class="text-sm text-muted-foreground">No uploads linked to this record yet.</p>

    <div v-else class="flex flex-col gap-3">
      <div class="flex flex-wrap gap-2">
        <UploadedFile v-for="upload in uploadableStore.items" :key="upload.id" :file="upload" :deletable="!readonly" @deleted="handleDeleted" @error="handleDeleteError" />
      </div>

      <div v-if="showPagination" class="pt-2">
        <Pagination v-model:page="uploadableStore.page" :items-per-page="uploadableStore.perPage" :total="uploadableStore.total ?? 0" :sibling-count="1" show-edges>
          <PaginationContent v-slot="{ items: paginationItems }">
            <PaginationFirst />
            <PaginationPrevious />

            <template v-for="(paginationItem, index) in paginationItems" :key="`${paginationItem.type}-${paginationItem.value ?? index}`">
              <PaginationItem v-if="paginationItem.type === 'page'" :value="paginationItem.value" :is-active="paginationItem.value === uploadableStore.page">
                {{ paginationItem.value }}
              </PaginationItem>
              <PaginationEllipsis v-else :index="index" />
            </template>

            <PaginationNext />
            <PaginationLast />
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationNext,
  PaginationPrevious,
} from '../../../../../../../resources/js/components/ui/pagination/index.js'
import UploadButton from '../UploadButton.vue'
import UploadedFile from '../UploadedFile.vue'
import { useUploadableStore } from '../../stores/uploadableStore.js'

const props = defineProps({
  uploadableId: {
    type: [Number, String],
    required: true,
  },
  uploadableType: {
    type: String,
    required: true,
  },
  context: {
    type: String,
    default: null,
  },
  perPage: {
    type: Number,
    default: 12,
  },
  multiple: {
    type: Boolean,
    default: true,
  },
  imagesOnly: {
    type: Boolean,
    default: false,
  },
  fileTypes: {
    type: [Array, String],
    default: null,
  },
  uploadLabel: {
    type: String,
    default: 'Upload files',
  },
  uploadingLabel: {
    type: String,
    default: 'Uploading...',
  },
  readonly: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['uploaded', 'deleted', 'error', 'upload-start', 'upload-finish'])

const statusMessage = ref('')

const resolvedContext = computed(() => {
  if (typeof props.context === 'string' && props.context.trim().length > 0) {
    return props.context.trim()
  }

  return `uploads_card_${String(props.uploadableType).replace(/[^A-Za-z0-9]/g, '_')}_${props.uploadableId}`
})

const uploadableStore = useUploadableStore(resolvedContext.value)

const uploadsCountLabel = computed(() => {
  const total = Number(uploadableStore.total ?? uploadableStore.items.length ?? 0)

  return `${total} linked`
})

const normalizedPerPage = computed(() => {
  const perPage = Number(props.perPage)

  if (!Number.isInteger(perPage) || perPage < 1) {
    return 12
  }

  return Math.min(perPage, 100)
})

const showPagination = computed(() => {
  const total = Number(uploadableStore.total ?? 0)

  if (total <= 0) {
    return false
  }

  return total > Number(uploadableStore.perPage ?? normalizedPerPage.value)
})

const loadErrorMessage = computed(() => {
  if (!uploadableStore.error) {
    return null
  }

  return uploadableStore.error.response?.data?.message ?? uploadableStore.error.message ?? 'Unable to load uploads.'
})

const resolveErrorMessage = (error, fallbackMessage) => {
  if (typeof error?.response?.data?.message === 'string' && error.response.data.message.length > 0) {
    return error.response.data.message
  }

  if (typeof error?.message === 'string' && error.message.length > 0) {
    return error.message
  }

  return fallbackMessage
}

const loadUploads = async () => {
  statusMessage.value = ''
  uploadableStore.perPage = normalizedPerPage.value

  await uploadableStore.fetchItemsForUploadable(props.uploadableId, props.uploadableType, {
    page: uploadableStore.page,
    per_page: normalizedPerPage.value,
  })
}

const resetPaginationAndLoad = async () => {
  const shouldLoadImmediately = uploadableStore.page === 1
  uploadableStore.page = 1

  if (shouldLoadImmediately) {
    await loadUploads()
  }
}

const handleUploadStart = () => {
  if (props.readonly) {
    return
  }

  statusMessage.value = 'Uploading file(s)...'
  emit('upload-start')
}

const handleUploaded = async (payload) => {
  if (props.readonly) {
    return
  }

  const uploadedItems = Array.isArray(payload) ? payload : [payload]
  statusMessage.value = `Upload completed: ${uploadedItems.length} file(s).`
  emit('uploaded', payload)
  emit('upload-finish', payload)
  await resetPaginationAndLoad()
}

const handleUploadError = (error) => {
  statusMessage.value = resolveErrorMessage(error, 'Unable to upload selected file(s).')
  emit('error', error)
  emit('upload-finish', error)
}

const handleDeleted = async (deletedFile) => {
  if (props.readonly) {
    return
  }

  uploadableStore.itemRemoveFromState(deletedFile?.id)
  statusMessage.value = `Deleted: ${deletedFile?.filename ?? 'file'}.`
  emit('deleted', deletedFile)

  if (typeof uploadableStore.total === 'number') {
    uploadableStore.total = Math.max(uploadableStore.total - 1, 0)
  }

  if (uploadableStore.items.length === 0 && uploadableStore.page > 1) {
    uploadableStore.page -= 1

    return
  }

  await loadUploads()
}

const handleDeleteError = (error) => {
  statusMessage.value = resolveErrorMessage(error, 'Unable to delete file.')
  emit('error', error)
}

watch(
  () => [props.uploadableId, props.uploadableType],
  async () => {
    await resetPaginationAndLoad()
  },
)

watch(
  () => props.perPage,
  async () => {
    await resetPaginationAndLoad()
  },
)

watch(
  () => uploadableStore.page,
  async (newPage, oldPage) => {
    if (newPage === oldPage) {
      return
    }

    await loadUploads()
  },
)

onMounted(async () => {
  await loadUploads()
})
</script>
